"use client"

import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Building2, ThumbsUp, CheckCircle2 } from "lucide-react"
import type { Review } from "@/types/review"
import { getAvatarColor, getInitials } from "@/utils/avatar-utils"
import { isMarkedAsHelpful } from "@/utils/helpful-uitils"

interface ReviewCardProps {
  review: Review
  onCardClick: (review: Review) => void
  onMarkHelpful: (reviewId: string) => void
}

export function ReviewCard({ review, onCardClick, onMarkHelpful }: ReviewCardProps) {
  const preview = review.fullReview?.length > 300 ? review.fullReview.substring(0, 300) : review.fullReview || "";
  return (
    <Card
      className="h-full cursor-pointer border border-gray-300 bg-white hover:bg-blue-50/60 shadow-sm"
      onClick={() => onCardClick(review)}
    >
      <CardHeader className="pb-3 px-5">
        <div className="flex items-start gap-3">
          <Avatar className={`w-10 h-10 ${getAvatarColor(review.name)}`}>
            <AvatarFallback className="text-white font-semibold bg-transparent">
              {getInitials(review.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-normal text-sm truncate">{review.name}</h4>
              {review.verified && (
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">{review.role}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {review.rating}/5
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 px-5">
        <h5 className="font-medium text-sm mb-2 line-clamp-2">{review.title}</h5>
        <p className="text-sm text-muted-foreground line-clamp-4 mb-3">{preview}</p>
        <div className="space-y-0.5">
          <div className="flex items-center gap-y-1 justify-between text-[0.7rem] text-muted-foreground">
            <div className="flex items-center min-w-0 gap-1">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{review.companySize}</span>
            </div>
            {isMarkedAsHelpful(review.id) ? (
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 text-xs text-muted-foreground text-blue-600 bg-blue-50 hover:text-blue-600 hover:bg-blue-50 h-6 px-2 flex-shrink-0"
              >
                <ThumbsUp className="w-3 h-3" />
                <span>{review.helpful}</span>
              </Button>
            ) : (<Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onMarkHelpful(review.id)
              }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-blue-600 hover:bg-blue-50 h-6 px-2 flex-shrink-0"
            >
              <ThumbsUp className="w-3 h-3" />
              <span>{review.helpful}</span>
            </Button>)}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
